import {
  DISCARDED_DIALPAD_INPUT_CHARACTERS,
  DTMF_MAX_CHARACTER_VALIDATION,
} from 'Modules/softphone/components/constants';
import { useMemo } from 'react';
import { CallButtonValidationState, UseCallButtonValidationParams } from './index.types';

/**
 * Hook to validate call button state based on dialpad input
 * @param dialpadDigits - Current dialpad input
 * @param hasCurrentCall - Whether there's an active call
 * @returns Validation state for the call button
 */
export const useCallButtonValidation = ({
  dialpadDigits,
  hasCurrentCall,
}: UseCallButtonValidationParams): CallButtonValidationState => {
  return useMemo(() => {
    const cleanedNumber =
      dialpadDigits?.replace(DISCARDED_DIALPAD_INPUT_CHARACTERS, '') || '';
    const isValidNumber = DTMF_MAX_CHARACTER_VALIDATION.test(cleanedNumber);

    // No input - show basic validation message
    if (!cleanedNumber.length) {
      return {
        disabled: true,
        helperText: 'Please type a phone number.',
        isValidNumber: false,
        cleanedNumber,
      };
    }

    // Invalid number - show character limit message
    if (!isValidNumber) {
      return {
        disabled: true,
        helperText: 'Please limit the phone number to\n21 characters or less.',
        isValidNumber: false,
        cleanedNumber,
      };
    }

    // Valid number - enable button and show tooltip if there's an active call
    return {
      disabled: false,
      helperText: hasCurrentCall ? 'Starting a new call holds your current call.' : undefined,
      isValidNumber: true,
      cleanedNumber,
    };
  }, [dialpadDigits, hasCurrentCall]);
};
